package com.example.event_management_system.ui.screens.events

import cafe.adriel.voyager.core.model.ScreenModel
import cafe.adriel.voyager.core.model.screenModelScope
import com.example.event_management_system.data.models.Event
import com.example.event_management_system.data.models.EventStatus
import com.example.event_management_system.data.models.Location
import com.example.event_management_system.data.models.User
import com.example.event_management_system.data.models.UserRole
import com.example.event_management_system.data.repository.IAuthRepository
import com.example.event_management_system.data.repository.IEventRepository
import com.example.event_management_system.data.repository.ILocationRepository
import com.example.event_management_system.data.repository.IUserRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class EventListState(
    val events: List<Event> = emptyList(),
    val filteredEvents: List<Event> = emptyList(),
    val locations: List<Location> = emptyList(),
    val selectedLocationId: String? = null,
    val selectedStatus: EventStatus? = null,
    val searchQuery: String = "",
    val currentUser: User? = null,
    val isLoading: Boolean = true,
    val error: String? = null
)

class EventListScreenModel(
    private val eventRepository: IEventRepository,
    private val locationRepository: ILocationRepository,
    private val userRepository: IUserRepository,
    private val authRepository: IAuthRepository
) : ScreenModel {

    private val _state = MutableStateFlow(EventListState())
    val state: StateFlow<EventListState> = _state.asStateFlow()

    init {
        loadData()
    }

    private fun loadData() {
        screenModelScope.launch {
            _state.update { it.copy(isLoading = true) }

            val userId = authRepository.getCurrentUserId()
            if (userId == null) {
                _state.update { it.copy(isLoading = false, error = "Not authenticated") }
                return@launch
            }

            val user = userRepository.getUser(userId)
            if (user == null) {
                _state.update { it.copy(isLoading = false, error = "User not found") }
                return@launch
            }

            _state.update { it.copy(currentUser = user) }

            // Load locations
            launch {
                val locationsFlow = if (user.role == UserRole.OWNER) {
                    locationRepository.getAllLocationsFlow()
                } else {
                    locationRepository.getLocationsByIdsFlow(user.locationIds)
                }

                locationsFlow.collect { locations ->
                    _state.update { currentState ->
                        val selectedId = currentState.selectedLocationId
                            ?: locations.firstOrNull()?.id
                        currentState.copy(
                            locations = locations,
                            selectedLocationId = selectedId
                        )
                    }
                    loadEvents()
                }
            }
        }
    }

    private fun loadEvents() {
        val locationId = _state.value.selectedLocationId ?: return

        screenModelScope.launch {
            eventRepository.getEventsFlow(locationId).collect { events ->
                _state.update { currentState ->
                    currentState.copy(
                        events = events,
                        isLoading = false,
                        error = null
                    )
                }
                applyFilters()
            }
        }
    }

    fun preSelectLocation(locationId: String) {
        _state.update { it.copy(selectedLocationId = locationId) }
    }

    fun selectLocation(locationId: String) {
        _state.update { it.copy(selectedLocationId = locationId) }
        loadEvents()
    }

    fun selectStatus(status: EventStatus?) {
        _state.update { it.copy(selectedStatus = status) }
        applyFilters()
    }

    fun updateSearchQuery(query: String) {
        _state.update { it.copy(searchQuery = query) }
        applyFilters()
    }

    private fun applyFilters() {
        val currentState = _state.value
        var filtered = currentState.events

        // Filter by status
        currentState.selectedStatus?.let { status ->
            filtered = filtered.filter { it.status == status }
        }

        // Filter by search query
        if (currentState.searchQuery.isNotBlank()) {
            val query = currentState.searchQuery.lowercase()
            filtered = filtered.filter { event ->
                event.customerName.lowercase().contains(query) ||
                event.customerEmail.lowercase().contains(query) ||
                event.customerPhone.contains(query)
            }
        }

        // Sort by date (upcoming first)
        filtered = filtered.sortedBy { it.eventDate }

        _state.update { it.copy(filteredEvents = filtered) }
    }

    fun refresh() {
        loadData()
    }
}
